'use client'

import { useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { httpBatchLink } from '@trpc/client'
import { trpc } from '@/lib/trpc'
import superjson from 'superjson'
import { SessionProvider } from 'next-auth/react'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ErrorReporterProvider } from '@/hooks/use-error-reporter'
import { LiveAnnouncer } from '@/components/ui/live-announcer'
import { LazyMotion, domAnimation, MotionConfig } from 'framer-motion'
import { SignupConversionTracker } from '@/components/analytics/signup-conversion-tracker'
import { GoogleAnalyticsIdentifier } from '@/components/analytics/google-analytics-identifier'
import { UtmTracker } from '@/components/analytics/utm-tracker'
import { SessionTracker } from '@/components/analytics/session-tracker'
import { PageTracker } from '@/components/analytics/page-tracker'

function getBaseUrl() {
  if (typeof window !== 'undefined') return ''
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`
  return process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:4000'
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 1000,
            refetchOnWindowFocus: false,
          },
        },
      })
  )

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${getBaseUrl()}/api/trpc`,
          transformer: superjson,
          // Include credentials (cookies) with requests for session auth
          fetch(url, options) {
            return fetch(url, {
              ...options,
              credentials: 'include',
            })
          },
        }),
      ],
    })
  )

  return (
    <SessionProvider>
      <ErrorReporterProvider>
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            {/* QA/automation motion kill-switch: NEXT_PUBLIC_QA_DISABLE_MOTION=1
                forces reducedMotion="always" so framer-motion resolves transitions
                instantly and AnimatePresence never blocks on an exit animation.
                Automated (hidden-document) browsers otherwise hang on
                `AnimatePresence mode="wait"` wizards — see docs/qa/isolation-setup.md. */}
            <MotionConfig
              reducedMotion={
                process.env.NEXT_PUBLIC_QA_DISABLE_MOTION === '1'
                  ? 'always'
                  : 'user'
              }
            >
              <LazyMotion features={domAnimation} strict>
                <TooltipProvider delayDuration={200}>
                  <UtmTracker />
                  <SignupConversionTracker />
                  <GoogleAnalyticsIdentifier />
                  <SessionTracker />
                  <PageTracker />
                  {children}
                  <Toaster position="top-right" />
                  <LiveAnnouncer />
                </TooltipProvider>
              </LazyMotion>
            </MotionConfig>
          </QueryClientProvider>
        </trpc.Provider>
      </ErrorReporterProvider>
    </SessionProvider>
  )
}

